/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useEffect, useState } from "react";
import { ApolloError, useLazyQuery } from "@apollo/client";
import { Maybe } from "graphql/jsutils/Maybe";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import {
  GetDistrictStoresParticipationQuery,
  GetOrgRegionsParticipationQuery,
  GetRegionDistrictsParticipationQuery,
  GetStoreUsersParticipationQuery,
  GetUserParticipationQuery,
} from "graphql/internal/generated";
import {
  GET_DISTRICT_STORES_PARTICIPATION,
  GET_ORG_REGIONS_PARTICIPATION,
  GET_REGION_DISTRICTS_PARTICIPATION,
  GET_STORE_USERS_PARTICIPATION,
  GET_USER_PARTICIPATION,
} from "graphql/internal/Queries";
import { RelayArray } from "config/UtilityTypes";
import { getLastDateOfMonth, getNodes, isNotNil } from "config/Utilities";
import {
  HasParentLocations,
  LeaderType,
  LocationCategory,
  LocationType,
  LocationUserType,
  MainLocation,
  SubLocationType,
} from "./types";

type PageInfo = {
  endCursor?: Maybe<string>;
  hasNextPage: boolean;
};

type CountOf<K extends string> = K extends `${infer T}s` ? `${T}Count` : never;

function getCount<K extends string>(
  obj: Record<CountOf<K>, number>,
  key: K
): number {
  return obj[`${key.slice(0, -1)}Count`];
}

type RawLocationGraphqlType<K extends string> = LocationType &
  Record<K, RelayArray<SubLocationType | LocationUserType>> & {
    leader?: Maybe<LeaderType>;
    childrenCompletedQuota?: Maybe<number>;
  } & Record<CountOf<K>, number> &
  HasParentLocations;

function extractParentLocation(
  gqlObject: HasParentLocations
): HasParentLocations {
  return Object.fromEntries(
    ["organization", "region", "district", "store"]
      .map((key) => {
        const record = gqlObject[key];
        return record ? [key, { name: record.name, id: record.id }] : undefined;
      })
      .filter(isNotNil)
  );
}

function extractLocationData<K extends string>(
  location: RawLocationGraphqlType<K>,
  subLocationsKey: K
): MainLocation {
  const name =
    location.name || `${location["firstName"]} ${location["lastName"]}`;
  return {
    id: location.id,
    name,
    quotaProgress: location.quotaProgress,
    quotaTarget: location.quotaTarget,
    isOnTrack: location.isOnTrack,
    childrenCompletedQuota: location.childrenCompletedQuota,
    subLocationCount: getCount(location, subLocationsKey),
    subLocations: getNodes(location[subLocationsKey]).map((loc) =>
      loc["name"]
        ? (loc as SubLocationType)
        : {
            ...loc,
            name: `${loc["firstName"]} ${loc["lastName"]}`,
            participatingUsers: loc.quotaProgress && 1,
            userCount: 1,
          }
    ),
    leader: location.leader || undefined,
    parentLocations: extractParentLocation(location),
  };
}
export default function useManagerDashboard({
  locationType,
  locationId,
  onError: errorCallback,
  resultsPerPage = 100,
}: {
  locationType: LocationCategory;
  locationId: string;
  onError: (e: ApolloError | string) => void;
  resultsPerPage?: number;
}) {
  const [location, setLocation] = useState<MainLocation>();
  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const onError = (e: ApolloError | string) => {
    console.error(
      `Error loading dashboard data for ${locationType} ${locationId}:`,
      e
    );
    setError(
      `Error loading dashboard data for ${locationType} (ID: ${locationId})`
    );
    errorCallback(e);
  };

  function onQueryComplete<K extends string>(
    location: Maybe<
      RawLocationGraphqlType<K> & Record<K, { pageInfo: PageInfo }>
    >,
    subLocationsKey: K
  ) {
    if (!location) {
      onError(`Unexpected data format: ${subLocationsKey} is not set`);
      return;
    }
    const locationData = extractLocationData(location, subLocationsKey);
    setLocation((current) => {
      if (!current) return locationData;
      if (current.id !== location.id) return current;
      const rawSubLocations = [
        ...current.subLocations,
        ...locationData.subLocations,
      ];
      // Make sure that there are no duplicate objects in subLocations array
      const subLocations = [
        ...new Map(rawSubLocations.map((item) => [item["id"], item])).values(),
      ];
      return { ...current, subLocations };
    });
    setPageInfo(location[subLocationsKey].pageInfo);
    setError(undefined);
  }

  const [getOrgParticipation, { loading: orgLoading }] =
    useLazyQuery<GetOrgRegionsParticipationQuery>(
      GET_ORG_REGIONS_PARTICIPATION,
      {
        fetchPolicy: "network-only",
        onCompleted: (result) => {
          onQueryComplete(result?.user?.organization, "regions");
        },
        onError,
      }
    );
  const [getRegionParticipation, { loading: regionLoading }] =
    useLazyQuery<GetRegionDistrictsParticipationQuery>(
      GET_REGION_DISTRICTS_PARTICIPATION,
      {
        fetchPolicy: "network-only",
        onCompleted: (result) => {
          onQueryComplete(result?.region, "districts");
        },
        onError,
      }
    );
  const [getDistrictParticipation, { loading: districtLoading }] =
    useLazyQuery<GetDistrictStoresParticipationQuery>(
      GET_DISTRICT_STORES_PARTICIPATION,
      {
        fetchPolicy: "network-only",
        onCompleted: (result) => {
          onQueryComplete(result?.district, "stores");
        },
        onError,
      }
    );
  const [getStoreParticipation, { loading: storeLoading }] =
    useLazyQuery<GetStoreUsersParticipationQuery>(
      GET_STORE_USERS_PARTICIPATION,
      {
        fetchPolicy: "network-only",
        onCompleted: (result) => {
          onQueryComplete(result?.store, "users");
        },
        onError,
      }
    );
  const [getUserParticipation, { loading: userLoading }] =
    useLazyQuery<GetUserParticipationQuery>(GET_USER_PARTICIPATION, {
      fetchPolicy: "network-only",
      onCompleted: ({ user }) => {
        const userAsLocation: MainLocation = {
          id: user.id,
          name: user.name,
          firstName: user.firstName,
          lastName: user.lastName,
          quotaProgress: user.quotaProgress,
          quotaTarget: user.quotaTarget,
          isOnTrack: user.isOnTrack,
          childrenCompletedQuota:
            user.quotaProgress === user.quotaTarget ? 1 : 0,
          subLocationCount: 0,
          subLocations: [],
          leader: user.store?.leader || undefined,
          parentLocations: extractParentLocation(user),
        };
        setLocation(userAsLocation);
        setPageInfo(undefined);
        setError(undefined);
      },
      onError,
    });

  useEffect(() => {
    setLoading(
      orgLoading ||
        regionLoading ||
        districtLoading ||
        storeLoading ||
        userLoading
    );
  }, [orgLoading, regionLoading, districtLoading, storeLoading, userLoading]);

  const loadLocationData = (n: number, cursor?: string) => {
    switch (locationType) {
      case "organization":
        getOrgParticipation({
          variables: {
            first: n,
            cursor,
          },
        });
        break;
      case "region":
        getRegionParticipation({
          variables: {
            getUserRegionRequest: { regionId: locationId },
            first: n,
            cursor,
          },
        });
        break;
      case "district":
        getDistrictParticipation({
          variables: {
            getUserDistrictRequest: { districtId: locationId },
            first: n,
            cursor,
          },
        });
        break;
      case "store":
        getStoreParticipation({
          variables: {
            getUserStoreRequest: { storeId: locationId },
            first: n,
            cursor,
          },
        });
        break;
      case "user":
        getUserParticipation({ variables: { userId: locationId } });
        break;
      default:
        console.error(
          "Trying to download data for unsupported type:",
          locationType
        );
        break;
    }
  };

  useEffect(() => {
    setLocation(undefined);
    setPageInfo(undefined);
    loadLocationData(resultsPerPage);
  }, [locationId, locationType]);

  const loadNextPage = (n: number = resultsPerPage) => {
    const loading =
      orgLoading ||
      regionLoading ||
      districtLoading ||
      storeLoading ||
      userLoading;
    if (!loading && pageInfo?.hasNextPage) {
      loadLocationData(n, pageInfo?.endCursor || undefined);
    }
  };

  const selfObject = useSelector((state: RootState) => state.selfObject);
  const activeQuota = selfObject.organizationActiveQuota;
  const quotaEndDate = activeQuota
    ? new Date(activeQuota.endDate)
    : getLastDateOfMonth();

  return {
    lastUpdated: selfObject.organizationCachedFieldsLastUpdated
      ? new Date(selfObject.organizationCachedFieldsLastUpdated)
      : null,
    location,
    loading,
    loadNextPage,
    hasNextPage: pageInfo?.hasNextPage || false,
    quotaEndDate,
    error: loading ? undefined : error,
  };
}
